/** Git panel: status, file-by-file diff viewer, commit, and push. */

import { useEffect, useState, useCallback } from "react";
import { GitService } from "../services";
import type { GitStatus } from "../types";

interface Props {
  refreshKey: number;
  onOpenFileDiff?: (path: string) => void;
}

/** Render a unified diff with added/removed line highlighting. */
function DiffView({ diff }: { diff: string }): JSX.Element {
  const lines = diff.split("\n");
  return (
    <pre className="git-diff scroll" style={{ margin: 0, padding: "4px 8px", fontSize: 12, lineHeight: 1.4 }}>
      {lines.map((line, i) => {
        let cls = "diff-line";
        if (line.startsWith("+++") || line.startsWith("---")) {
          cls += " diff-meta";
        } else if (line.startsWith("+")) {
          cls += " diff-add";
        } else if (line.startsWith("-")) {
          cls += " diff-del";
        } else if (line.startsWith("@@")) {
          cls += " diff-hunk";
        }
        return (
          <div key={i} className={cls}>
            <span className="diff-content">{line || " "}</span>
          </div>
        );
      })}
    </pre>
  );
}

const BUILD_ARTIFACT_EXTS = [
  "_minted",
  ".aux",
  ".fdb_latexmk",
  ".fls",
  ".lof",
  ".lot",
  ".out",
  ".toc",
  ".xdv",
  ".synctex.gz",
  ".log",
];

function isBuildArtifact(path: string): boolean {
  const lower = path.toLowerCase();
  return BUILD_ARTIFACT_EXTS.some((ext) => lower.includes(ext));
}

export function GitPanel({ refreshKey, onOpenFileDiff }: Props): JSX.Element {
  const [status, setStatus] = useState<GitStatus | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileDiff, setFileDiff] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [loadingDiff, setLoadingDiff] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const s = await GitService.status();
      setStatus(s);
      setSelectedFile(null);
      setFileDiff("");
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : "Git error");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refreshKey, refresh]);

  /** Load the diff for a single file when clicked and open in editor. */
  const handleFileClick = useCallback(async (path: string): Promise<void> => {
    setSelectedFile(path);
    setLoadingDiff(true);
    setFileDiff("");
    try {
      const res = await GitService.diffFile(path);
      setFileDiff(res.diff || "(no changes)");
    } catch (err) {
      setFileDiff(err instanceof Error ? err.message : "Failed to load diff");
    } finally {
      setLoadingDiff(false);
    }
    if (onOpenFileDiff) {
      onOpenFileDiff(path);
    }
  }, [onOpenFileDiff]);

  const commit = async (push: boolean): Promise<void> => {
    if (!message.trim()) {
      setFeedback("Commit message is required.");
      return;
    }
    setBusy(true);
    setFeedback("");
    try {
      const res = await GitService.commit(message.trim(), push);
      setFeedback(res.summary);
      setMessage("");
      await refresh();
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : "Commit failed");
    } finally {
      setBusy(false);
    }
  };

  const displayFiles = (status?.files || []).filter((f) => !isBuildArtifact(f.path));
  const isClean = displayFiles.length === 0;

  return (
    <div className="git-panel" style={{ display: "flex", height: "100%" }}>
      {/* Left: file list */}
      <div style={{ width: 260, borderRight: "1px solid var(--border)", overflow: "auto", flexShrink: 0 }}>
        <div style={{ padding: "4px 8px", fontWeight: "bold", borderBottom: "1px solid var(--border)" }}>
          Changes ({displayFiles.length})
        </div>
        <div style={{ padding: "4px 0" }}>
          {isClean && (
            <div className="empty" style={{ padding: "8px" }}>No changes.</div>
          )}
          {displayFiles.map((f) => (
            <div
              key={f.path}
              className={`git-file ${selectedFile === f.path ? "active" : ""}`}
              onClick={() => void handleFileClick(f.path)}
              style={{
                cursor: "pointer",
                padding: "3px 8px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: selectedFile === f.path ? "var(--active-bg)" : "transparent",
              }}
            >
              <span className={`badge ${f.status[0]}`}>{f.status}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.path}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Right: diff viewer + commit */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "4px 8px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <strong>Branch:</strong> {status?.branch ?? "—"}
          {status && status.ahead > 0 && <span>(ahead {status.ahead})</span>}
          {status && status.behind > 0 && <span>(behind {status.behind})</span>}
          <span style={{ flex: 1 }} />
          <button onClick={refresh} disabled={busy} style={{ padding: "2px 8px" }}>Refresh</button>
        </div>

        {/* Diff viewer */}
        <div style={{ flex: 1, overflow: "auto", background: "var(--bg-secondary)" }}>
          {!selectedFile && (
            <div className="empty" style={{ padding: 16 }}>
              Select a file to view its diff.
            </div>
          )}
          {selectedFile && loadingDiff && (
            <div className="empty" style={{ padding: 16 }}>Loading diff...</div>
          )}
          {selectedFile && !loadingDiff && fileDiff && (
            <DiffView diff={fileDiff} />
          )}
        </div>

        {/* Commit area */}
        <div style={{ borderTop: "1px solid var(--border)", padding: "6px 8px" }}>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              type="text"
              placeholder="Commit message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={{ flex: 1, padding: "4px 6px" }}
            />
            <button onClick={() => commit(false)} disabled={busy}>Commit</button>
            <button className="primary" onClick={() => commit(true)} disabled={busy}>
              Commit &amp; Push
            </button>
          </div>
          {feedback && <div className="empty" style={{ padding: "4px 0" }}>{feedback}</div>}
        </div>
      </div>
    </div>
  );
}
